import React, { CSSProperties, forwardRef, Fragment, ReactNode, useMemo } from "react";
import { AutoSizer, CellMeasurer, CellMeasurerCache, CellMeasurerCacheParams, IndexRange, List, ListProps, OnScrollCallback } from "react-virtualized";

import { bemClassNames } from "@multiweb/reactUtils";
import { ForwardRefType, KeyGeneratorType, RowRenderer } from "@multiweb/types/react";
import { Role } from "@multiweb/uiShared/Role";
import { DataGridVariant, VirtualizedListStickyComponentCreator } from "../types";

export type VirtualizedListProps<T> = {
    /** Data pro vykreslení položek. */
    readonly items: ReadonlyArray<T>;
    /** Funkce pro generování klíčů položek. */
    readonly keyGenerator: KeyGeneratorType<T>;
    /** Funkce pro vykreslení položek. */
    readonly itemRenderer: RowRenderer<T>;
    /** Parametry pro cache rozměrů položek. */
    readonly cellMeasureCacheParams: CellMeasurerCacheParams;
    /** Zaskroluje na daný index. */
    readonly scrollToIndex?: number;
    /** Nastavuje zarovnání scrollu. */
    readonly scrollToAlignment?: ListProps["scrollToAlignment"];
    /** Odhad výšky záznamu. */
    readonly estimatedRowSize?: number;
    /** Upravuje výchozí chování vzhledu. */
    readonly variant?: DataGridVariant;
    /** Callback volaný při scrollování listem. */
    readonly onScroll?: OnScrollCallback;
    /** Funkce pro vytvoření hlavičky. */
    readonly stickyHeaderCreator?: VirtualizedListStickyComponentCreator;
    /** Funkce pro vytvoření patičky. */
    readonly stickyFooterCreator?: VirtualizedListStickyComponentCreator;
    /** Funkce volaná po vykreslení řádků - interní využití. */
    readonly onRowsRendered?: (indexRange: IndexRange) => void;
    /** Funkce pro vykreslení obsahu namísto položek v případě, že nejsou dostupná žádná data. */
    readonly noRowsRenderer?: () => JSX.Element;
};

const STICKY_HEADER_KEY = "virtualized-list-sticky-header";
const STICKY_FOOTER_KEY = "virtualized-list-sticky-footer";

const classNames = bemClassNames("virtualized-list-component");

export const VirtualizedList: ForwardRefType<List, VirtualizedListProps<any>> = forwardRef<List, VirtualizedListProps<any>>(({
    items,
    itemRenderer,
    keyGenerator,
    cellMeasureCacheParams,
    estimatedRowSize,
    scrollToIndex,
    scrollToAlignment,
    variant = DataGridVariant.default,
    onScroll,
    stickyHeaderCreator,
    stickyFooterCreator,
    onRowsRendered,
    noRowsRenderer,
}, ref) => {
    /**
     * Cache má jako klíče indexy, proto se musí vytvořit znovu při změně dat, jinak by používala výšky nesprávných položek.
     * Cache slouží pro jednu sadu položek při jejich scrollování.
     */
    const cache = new CellMeasurerCache(cellMeasureCacheParams);
    const hasHeader = !!stickyHeaderCreator;
    const hasFooter = !!stickyFooterCreator;
    const rowCount = useMemo(
        () => {
            if (items.length > 0) {
                return items.length + (hasHeader ? 1 : 0) + (hasFooter ? 1 : 0);
            }

            return items.length;
        },
        [items, hasHeader, hasFooter],
    );

    return (
        <div className={classNames({ variant })}>
            <AutoSizer>
                {({ width, height }) => (
                    <Fragment>
                        {stickyHeaderCreator && (
                            <div className={classNames("sticky-header")}>
                                {stickyHeaderCreator(width)}
                            </div>
                        )}
                        <List
                            tabIndex={null}
                            rowHeight={cache.rowHeight}
                            deferredMeasurementCache={cache}
                            {...{ width, height, rowCount, onRowsRendered, ref, noRowsRenderer, estimatedRowSize, scrollToIndex, scrollToAlignment, onScroll }}
                            role={Role.rowGroup}
                            rowRenderer={({ index, style, parent }) => {
                                const key = internalKeyGenerator(index, items, keyGenerator, hasHeader, hasFooter);
                                return (
                                    <CellMeasurer
                                        key={key}
                                        cache={cache}
                                        parent={parent}
                                        index={index}
                                    >
                                        {internalItemRenderer(index, items, itemRenderer, style, width, stickyHeaderCreator, stickyFooterCreator)}
                                    </CellMeasurer>
                                );
                            }}
                        />
                        {stickyFooterCreator && (
                            <div className={classNames("sticky-footer")}>
                                {stickyFooterCreator(width)}
                            </div>
                        )}
                    </Fragment>
                )}
            </AutoSizer>
        </div>
    );
});

/**
 * Vygeneruje klíč pro položku listu. Generuje speciální klíč pro hlavičku a patičku. Pro zbývající položky listu používá generátor
 * dodaný v props.
 */
const internalKeyGenerator = <T,>(index: number, items: ReadonlyArray<T>, keyGenerator: KeyGeneratorType<T>, hasHeader: boolean, hasFooter: boolean) => {
    const prefixItemsCount = hasHeader ? 1 : 0;
    if (hasHeader && index === 0) {
        return STICKY_HEADER_KEY;
    } else if (hasFooter && index === (items.length + prefixItemsCount)) {
        return STICKY_FOOTER_KEY;
    } else {
        const itemIndex = index - prefixItemsCount;
        return keyGenerator(items[itemIndex], itemIndex, items);
    }
};

/**
 * Vykreslí položku listu. Řeší vykreslení hlavičky/patičky na první/poslední pozici, pokud jsou specifikované. Zbylé položky jsou vykresleny
 * pomocí funkce dodané v props.
 */
const internalItemRenderer = <T,>(
    index: number,
    items: ReadonlyArray<T>,
    itemRenderer: RowRenderer<T>,
    style: CSSProperties,
    width: number,
    stickyHeaderCreator?: VirtualizedListStickyComponentCreator,
    stickyFooterCreator?: VirtualizedListStickyComponentCreator,
): ReactNode => {
    const prefixItemsCount = stickyHeaderCreator ? 1 : 0;
    if (stickyHeaderCreator && index === 0) {
        return (
            <div style={style} className={classNames("inner-sticky-header")}>
                {stickyHeaderCreator(width)}
            </div>
        );
    } else if (stickyFooterCreator && index === (items.length + prefixItemsCount)) {
        return (
            <div style={style} className={classNames("inner-sticky-footer")}>
                {stickyFooterCreator(width)}
            </div>
        );
    } else {
        const itemIndex = index - prefixItemsCount;
        return (
            <div style={style}>
                {itemRenderer(items[itemIndex], itemIndex, items)}
            </div>
        );
    }
};
